import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import Tooltip from '@mui/material/Tooltip';
import { OrganizationIdentifierKindEnum } from '@e-socna/data-layer';
import {
  deriveSirenFromSiret,
  formatSiren,
  formatSiret,
} from '@e-socna/third-parties';
import { buildPath, nameof } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { PATH_ORGANIZATION_DEFAULT } from '../../../app/constants/paths.constant';
import type { ReferencesContext_GenericStatus } from '../../../contexts/ReferencesContext';

import { UseCase } from '../enums/use-case.enum';
import type { ThirdPartiesCmp_Organization } from '../fragments/organization.fragment';

import { THIRD_PARTY_STATUS_ID } from './get-third-party-columns';

interface ActionProps {
  readonly id: Id;
}

const Action = ({ id }: Readonly<ActionProps>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_ORGANIZATION_DEFAULT, { id });
    void navigate(path);
  }, [navigate, id]);

  const actionLabel = "Consulter l'Organisation";

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getOrganizationColumns = (
  useCase: UseCase,
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<ThirdPartiesCmp_Organization>[] => {
  const columns: readonly GridColDef<ThirdPartiesCmp_Organization>[] = [
    ...(useCase === UseCase.Visualize
      ? ([
          {
            field: 'actions',
            headerName: 'Actions',
            description: 'Actions',
            editable: false,
            sortable: false,
            type: 'actions',
            getActions: (
              params: Readonly<GridRowParams<ThirdPartiesCmp_Organization>>,
            ) => [<Action id={params.row._id} key="consult" />],
          },
        ] as const)
      : EMPTY_ARRAY),
    {
      field: 'siren',
      headerName: 'SIREN',
      width: 200,
      description: 'SIREN',
      editable: false,
      sortable: true,
      type: 'string',
      valueGetter: (
        _: unknown,
        row: Readonly<ThirdPartiesCmp_Organization>,
      ) => {
        const { identifier } = row;

        if (!identifier) {
          return null;
        }

        return identifier.kind === OrganizationIdentifierKindEnum.Siret
          ? deriveSirenFromSiret(identifier.value)
          : identifier.value;
      },
      valueFormatter: (value: string | null | undefined) =>
        value ? formatSiren(value) : value,
    },
    {
      field: 'siret',
      headerName: 'SIRET',
      width: 250,
      description: 'SIRET',
      editable: false,
      sortable: true,
      type: 'string',
      valueGetter: (
        _: unknown,
        row: Readonly<ThirdPartiesCmp_Organization>,
      ) => {
        const { identifier } = row;

        return identifier?.kind === OrganizationIdentifierKindEnum.Siret
          ? identifier.value
          : null;
      },
      valueFormatter: (value: string | null | undefined) =>
        value ? formatSiret(value) : value,
    },
    {
      field: nameof<ThirdPartiesCmp_Organization>('name'),
      headerName: 'Raison Sociale',
      width: 350,
      description: 'Raison Sociale',
      editable: false,
      sortable: true,
      type: 'string',
    },
    {
      field: nameof<ThirdPartiesCmp_Organization>('tradingName'),
      headerName: 'Nom Commercial',
      width: 350,
      description: 'Nom Commercial',
      editable: false,
      sortable: true,
      type: 'string',
    },
    {
      field: THIRD_PARTY_STATUS_ID,
      headerName: 'Statut',
      width: 200,
      description: 'Statut',
      editable: false,
      sortable: true,
      hideable: true,
      type: 'singleSelect',
      valueGetter: (_: unknown, row: Readonly<ThirdPartiesCmp_Organization>) =>
        genericStatuses.find(
          (genericStatus) => genericStatus._id === row.statusId,
        )?.code ?? 'Inconnu',
      valueOptions: genericStatuses.map((genericStatus) => ({
        value: genericStatus.code,
        label: genericStatus.label,
      })),
    },
  ] as const;

  return columns;
};
